import { REGION_CONFIGS, type RegionConfig, type RegionCode } from "./region";

export interface AppConfig {
    apiUri: string;
    appEnv: "prod" | "development" | "local";
    debugMode: boolean;
    apolloDevTools: boolean;
    countryCode: RegionCode;
    region: RegionConfig;
}

const parseBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
    if (!value) return defaultValue;
    return value.toLowerCase() === "true" || value === "1";
};

const parseAppEnv = (value: string | undefined): AppConfig["appEnv"] => {
    if (!value) return "local";

    const normalizedValue = value.toLowerCase();
    const validEnvs: AppConfig["appEnv"][] = ["prod", "development", "local"];

    if (validEnvs.includes(normalizedValue as AppConfig["appEnv"])) {
        return normalizedValue as AppConfig["appEnv"];
    }

    console.warn(`Invalid VITE_APP_ENV value: ${value}. Defaulting to 'local'`);
    return "local";
};

const getCountryCode = (): RegionCode => {
    // Detect from URL: "/AU/agent" -> "AU"
    const firstSegment = window.location.pathname.split("/")[1]?.toUpperCase();
    const supportedRegions: RegionCode[] = ["AU", "US"];

    if (firstSegment && supportedRegions.includes(firstSegment as RegionCode)) {
        return firstSegment as RegionCode;
    }

    // Fallback to Env for local development
    return (import.meta.env.VITE_COUNTRY_CODE?.toUpperCase() as RegionCode) || "AU";
};

const getApiUri = (countryCode: string): string => {
    const uri = import.meta.env.VITE_API_URI;

    if (!uri) {
        console.warn("VITE_API_URI is not set, falling back to localhost:8000");
        return `http://localhost:8000/${countryCode}`;
    }

    try {
        const url = new URL(uri);
        // Append country code to the path
        if (!url.pathname.includes(`/${countryCode}`)) {
            url.pathname = url.pathname.replace(/\/$/, "") + `/${countryCode}`;
        }
        return url.toString().replace(/\/$/, "");
    } catch {
        console.error(`Invalid VITE_API_URI: ${uri}. Falling back to localhost:8000`);
        return `http://localhost:8000/${countryCode}`;
    }
};

const detectedCountry = getCountryCode();

export const applicationConfig: AppConfig = {
    countryCode: detectedCountry,
    region: REGION_CONFIGS[detectedCountry],
    apiUri: getApiUri(detectedCountry),
    appEnv: parseAppEnv(import.meta.env.VITE_APP_ENV),
    debugMode: parseBoolean(import.meta.env.VITE_DEBUG_MODE, import.meta.env.DEV),
    apolloDevTools: parseBoolean(import.meta.env.VITE_APOLLO_DEVTOOLS, import.meta.env.DEV),
};

export const isLocal = applicationConfig.appEnv === "local";
export const isDevelopment = applicationConfig.appEnv === "development";

const validateConfig = (): void => {
    const errors: string[] = [];

    if (!applicationConfig.apiUri) {
        errors.push("VITE_API_URI is required");
    }

    if (isDevelopment && applicationConfig.apiUri.includes("localhost")) {
        errors.push("Development build should not use localhost API URI");
    }

    if (errors.length > 0) {
        console.error("❌ Configuration validation failed:");
        errors.forEach((error) => {
            console.error(`  - ${error}`);
        });

        if (isDevelopment) {
            throw new Error("Invalid development applicationConfiguration");
        }
    }
};

validateConfig();
